// Librairies
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal, WritableSignal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

// Variables
// The grays, from white to black.
export const NEUTRAL_COLORS: readonly string[] = ['#FFFFFF', '#F3F4F6', '#D1D5DB', '#9CA3AF', '#6B7280', '#374151', '#1F2937', '#000000'];

// One column per hue (red, orange, amber, green, teal, blue, violet, pink), one row per shade: light, vivid, dark.
export const HUE_COLORS: readonly string[] = [
  '#FCA5A5', '#FDBA74', '#FCD34D', '#86EFAC', '#5EEAD4', '#93C5FD', '#C4B5FD', '#F9A8D4',
  '#EF4444', '#F97316', '#F59E0B', '#22C55E', '#14B8A6', '#3B82F6', '#8B5CF6', '#EC4899',
  '#B91C1C', '#C2410C', '#B45309', '#15803D', '#0F766E', '#1D4ED8', '#6D28D9', '#BE185D',
];

// Classes
// "#abc" or "abc123" → "#AABBCC"-like, null when it is not a hex color.
export function normalizeHex(text: string): string | null {
  let hex = text.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(hex)) hex = hex.split('').map(char => char + char).join('');
  return /^[0-9a-f]{6}$/i.test(hex) ? `#${hex.toUpperCase()}` : null;
}

// A swatch and its label; its panel offers the theme color ("auto", an empty value), the palettes and any color.
@Component({
  selector: 'lib-color-picker',
  standalone: true,
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MatIconModule,
    TranslateModule
  ],
  templateUrl: './color-picker.component.html',
  styleUrl: './color-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ColorPickerComponent {
  // Variables
  @Input() value: string | null = null;              // '' or null: the theme color
  @Input() label: string = '';                       // i18n key, none: the swatch alone
  @Input() allowAuto: boolean = false;
  @Output() valueChange: EventEmitter<string> = new EventEmitter();

  readonly neutrals = NEUTRAL_COLORS;
  readonly hues = HUE_COLORS;
  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  // Signals
  readonly open: WritableSignal<boolean> = signal(false);

  get isAuto(): boolean {
    return !this.value;
  }

  isSelected(color: string): boolean {
    return !!this.value && this.value.toUpperCase() === color;
  }

  toggle(): void {
    this.open.set(!this.open());
  }

  choose(color: string, close = true): void {
    this.value = color;
    this.valueChange.emit(color);
    if (close) this.open.set(false);
  }

  // A typed hex color, once complete.
  typed(text: string): void {
    const color = normalizeHex(text);
    if (color) this.choose(color, false);
  }
}
